/* DELGA — Shopify theme
   Vanilla JS only. No frameworks. No external deps.
   The store has Shopify's storefront captcha enabled, so contact forms
   submit natively to /contact — the captcha runs as part of that submit
   and the page reloads. delga.js validates the form before the captcha
   sees it (cancelling invalid submits) and, after Shopify redirects back
   with ?contact_posted=true, shows the success toast. */

(function () {
  'use strict';

  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ───────── Theme toggle ─────────
     Initial theme is set by an inline script in <head> (avoids FOUC).
     Default is dark. We just handle the click + persistence here. */
  const THEME_KEY = 'delga-theme';
  const themeToggle = $('#theme-toggle');

  function currentTheme() {
    return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
  }
  function applyTheme(theme) {
    document.documentElement.setAttribute('data-theme', theme);
    if (themeToggle) {
      themeToggle.setAttribute(
        'aria-label',
        theme === 'dark' ? 'Bytt til lyst tema' : 'Bytt til mørkt tema'
      );
    }
    try { localStorage.setItem(THEME_KEY, theme); } catch (e) { /* private mode etc. */ }
  }
  // Set the correct aria-label on first load
  applyTheme(currentTheme());

  if (themeToggle) {
    themeToggle.addEventListener('click', () => {
      applyTheme(currentTheme() === 'dark' ? 'light' : 'dark');
    });
  }

  /* ───────── Mobile drawer ───────── */
  const hamburger = $('#hamburger');
  const drawer    = $('#mobile-drawer');

  function setDrawer(open) {
    if (!hamburger || !drawer) return;
    hamburger.setAttribute('aria-expanded', String(open));
    hamburger.setAttribute('aria-label', open ? 'Lukk meny' : 'Åpne meny');
    if (open) {
      drawer.hidden = false;
      requestAnimationFrame(() => drawer.classList.add('is-open'));
    } else {
      drawer.classList.remove('is-open');
      drawer.hidden = true;
    }
  }

  if (hamburger) {
    hamburger.addEventListener('click', () => {
      const isOpen = hamburger.getAttribute('aria-expanded') === 'true';
      setDrawer(!isOpen);
    });
  }
  // Close drawer on link click (mobile nav)
  $$('#mobile-drawer a').forEach(a => a.addEventListener('click', () => setDrawer(false)));
  // Close on Escape
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && hamburger?.getAttribute('aria-expanded') === 'true') setDrawer(false);
  });

  /* ───────── Header transparent ↔ solid on scroll ─────────
     Header sits over the hero with no background. Once the user scrolls
     past most of the hero we add .is-scrolled so the header gets its
     solid theme backdrop and dark text again. */
  const header = $('.site-header');
  const hero = $('.hero');
  function updateHeaderState() {
    if (!header) return;
    // Trigger ~80% of the way through the hero so the change happens
    // before the hero exits the viewport.
    const triggerY = hero ? hero.offsetHeight * 0.7 : 80;
    header.classList.toggle('is-scrolled', window.scrollY > triggerY);
  }
  updateHeaderState();
  window.addEventListener('scroll', updateHeaderState, { passive: true });
  window.addEventListener('resize', updateHeaderState);

  /* ───────── Hero slider ─────────
     Cross-fades between hero slides. Auto-advances every ~6s; pauses on
     hover / focus / when tab is hidden. Dots click + keyboard. Respects
     prefers-reduced-motion (no auto-advance, manual still works). */
  const slider = $('#hero-slider');
  const slides = $$('.hero-slide', slider || document);
  const dots   = $$('.hero-dot');
  if (slider && slides.length > 1) {
    let idx = 0;
    let timer = null;
    const INTERVAL = 6000;
    const reduced  = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function show(next) {
      if (next === idx) return;
      slides[idx].classList.remove('is-active');
      dots[idx]?.classList.remove('is-active');
      dots[idx]?.setAttribute('aria-selected', 'false');
      idx = (next + slides.length) % slides.length;
      slides[idx].classList.add('is-active');
      dots[idx]?.classList.add('is-active');
      dots[idx]?.setAttribute('aria-selected', 'true');
    }
    function advance() { show(idx + 1); }
    function start()   { if (!reduced && !timer) timer = setInterval(advance, INTERVAL); }
    function stop()    { if (timer) { clearInterval(timer); timer = null; } }

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => { show(i); stop(); start(); });
      dot.addEventListener('keydown', e => {
        if (e.key === 'ArrowRight') { show(idx + 1); stop(); start(); }
        if (e.key === 'ArrowLeft')  { show(idx - 1); stop(); start(); }
      });
    });

    slider.addEventListener('mouseenter', stop);
    slider.addEventListener('mouseleave', start);
    slider.addEventListener('focusin', stop);
    slider.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', () => {
      document.hidden ? stop() : start();
    });

    start();
  }

/* ───────── Split hero — per-panel sliders ─────────
     Each .split-panel runs its own independent slider over its
     .split-slide children with its own .split-dot controls. A panel with
     a single slide stays static. The two panels are staggered so they
     don't cross-fade in lockstep. Respects prefers-reduced-motion. */
  function initSplitPanel(panel, offset) {
    const slides = $$('.split-slide', panel);
    const dots   = $$('.split-dot', panel);
    if (slides.length < 2) return;
    let idx = 0;
    let timer = null;
    const INTERVAL = 6500;
    const reduced  = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function show(next) {
      next = (next + slides.length) % slides.length;
      if (next === idx) return;
      slides[idx].classList.remove('is-active');
      if (dots[idx]) { dots[idx].classList.remove('is-active'); dots[idx].setAttribute('aria-selected', 'false'); }
      idx = next;
      slides[idx].classList.add('is-active');
      if (dots[idx]) { dots[idx].classList.add('is-active'); dots[idx].setAttribute('aria-selected', 'true'); }
    }
    function advance() { show(idx + 1); }
    function start()   { if (!reduced && !timer) timer = setInterval(advance, INTERVAL); }
    function stop()    { if (timer) { clearInterval(timer); timer = null; } }

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => { show(i); stop(); start(); });
      dot.addEventListener('keydown', e => {
        if (e.key === 'ArrowRight') { show(idx + 1); stop(); start(); }
        if (e.key === 'ArrowLeft')  { show(idx - 1); stop(); start(); }
      });
    });

    panel.addEventListener('mouseenter', stop);
    panel.addEventListener('mouseleave', start);
    panel.addEventListener('focusin', stop);
    panel.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', () => { document.hidden ? stop() : start(); });

    if (!reduced && offset) { setTimeout(start, offset); } else { start(); }
  }
  $$('.split-panel').forEach((panel, i) => initSplitPanel(panel, i * 3250));

  function headerOffset() { return header ? header.getBoundingClientRect().height : 0; }

  /* ───────── Contact modal ─────────
     The forms used to live in an inline #kontakt section; they now open
     in a <dialog>. Any link to #bestill / #tilbud / #kontakt opens the
     modal with the correct tab active. Close via X button, ESC (native),
     or click on the backdrop. */
  const contactModal = $('#contact-modal');
  const contactModalClose = $('#contact-modal-close');
  function openContactModal(tabId) {
    if (!contactModal) return;
    activateTab(tabId || 'tab-bestill');
    if (typeof contactModal.showModal === 'function') {
      if (!contactModal.open) contactModal.showModal();
    } else {
      contactModal.setAttribute('open', '');
    }
  }
  function closeContactModal() {
    if (!contactModal) return;
    if (typeof contactModal.close === 'function') contactModal.close();
    else contactModal.removeAttribute('open');
  }
  contactModalClose?.addEventListener('click', closeContactModal);
  // Backdrop click — close when the user clicks outside the dialog's
  // inner panel (the click bubbles from the <dialog> itself, not its child).
  contactModal?.addEventListener('click', e => {
    if (e.target === contactModal) closeContactModal();
  });

  document.addEventListener('click', e => {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;
    const id = a.getAttribute('href');
    if (id === '#' || id.length < 2) return;

    // Hash links that point at the (now removed) forms open the modal
    // instead of scrolling to a section.
    if (id === '#bestill') { e.preventDefault(); openContactModal('tab-bestill'); return; }
    if (id === '#tilbud')  { e.preventDefault(); openContactModal('tab-tilbud');  return; }
    if (id === '#kontakt') { e.preventDefault(); openContactModal('tab-bestill'); return; }

    const target = document.querySelector(id);
    if (!target) return;
    e.preventDefault();
    const y = target.getBoundingClientRect().top + window.pageYOffset - headerOffset() - 8;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: y, behavior: reduced ? 'auto' : 'smooth' });
  });

  /* ───────── Reveal-on-scroll ───────── */
  const reveals = $$('.reveal');
  if ('IntersectionObserver' in window && reveals.length) {
    const io = new IntersectionObserver(entries => {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting) {
          // Stagger by index within the same parent
          const delay = Math.min(i * 60, 360);
          entry.target.style.transitionDelay = delay + 'ms';
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    reveals.forEach(el => io.observe(el));
  } else {
    reveals.forEach(el => el.classList.add('is-visible'));
  }

  /* ───────── Tabs (Bestill time / Få tilbud) ───────── */
  const tabs   = $$('.tab');
  const panels = $$('.form-panel');

  function activateTab(tabId) {
    const tab = document.getElementById(tabId);
    if (!tab) return;
    tabs.forEach(t => {
      const active = t === tab;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-selected', String(active));
      t.tabIndex = active ? 0 : -1;
    });
    const panelId = tab.getAttribute('aria-controls');
    panels.forEach(p => {
      const active = p.id === panelId;
      p.classList.toggle('is-active', active);
      p.hidden = !active;
    });
  }

  tabs.forEach(t => {
    t.addEventListener('click', () => activateTab(t.id));
    t.addEventListener('keydown', e => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const idx = tabs.indexOf(t);
      const next = e.key === 'ArrowRight'
        ? tabs[(idx + 1) % tabs.length]
        : tabs[(idx - 1 + tabs.length) % tabs.length];
      activateTab(next.id);
      next.focus();
    });
  });

  /* ───────── Form validation + stubbed submit ───────── */
  function setError(field, message) {
    field.classList.toggle('has-error', !!message);
    const input = field.querySelector('input, select, textarea');
    if (input) input.setAttribute('aria-invalid', message ? 'true' : 'false');
    const err = field.querySelector('.error');
    if (err) err.textContent = message || '';
  }

  function validate(form) {
    let firstInvalid = null;
    $$('.field', form).forEach(field => setError(field, ''));

    $$('input[required], textarea[required], select[required]', form).forEach(input => {
      const field = input.closest('.field');
      if (!input.value.trim()) {
        setError(field, 'Dette feltet er påkrevd.');
        firstInvalid ||= input;
      }
    });

    const email = form.querySelector('input[type="email"]');
    if (email && email.value.trim()) {
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim());
      if (!ok) {
        setError(email.closest('.field'), 'Vennligst oppgi en gyldig e-postadresse.');
        firstInvalid ||= email;
      }
    }

    const tel = form.querySelector('input[type="tel"]');
    if (tel && tel.value.trim()) {
      const digits = tel.value.replace(/\D/g, '');
      if (digits.length < 6) {
        setError(tel.closest('.field'), 'Vennligst oppgi et gyldig telefonnummer.');
        firstInvalid ||= tel;
      }
    }

    return firstInvalid;
  }

  const toast = $('#toast');
  const toastIcon = toast ? toast.querySelector('.toast-icon') : null;
  let toastTimer;
  const TOAST_ICONS = {
    success: '<svg viewBox="0 0 24 24" width="22" height="22"><path fill="currentColor" d="m9 16.2-3.5-3.5L4 14.2l5 5 11-11-1.4-1.4Z"/></svg>',
    error:   '<svg viewBox="0 0 24 24" width="22" height="22"><path fill="currentColor" d="M12 2 1 21h22L12 2Zm-1 6h2v6h-2V8Zm0 8h2v2h-2v-2Z"/></svg>'
  };
  // type: 'success' (default) | 'error'
  function showToast(message, type) {
    if (!toast) return;
    const kind = type === 'error' ? 'error' : 'success';
    toast.classList.toggle('is-error', kind === 'error');
    if (toastIcon) toastIcon.innerHTML = TOAST_ICONS[kind];
    toast.querySelector('.toast-text').textContent = message;
    toast.hidden = false;
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => { toast.hidden = true; }, 250);
    }, 4500);
  }

  // Success toast copy depends on which form was sent — a booking
  // ("timebestilling") vs. a quote request ("forespørsel").
  function successMessage(form) {
    return form.id === 'form-tilbud'
      ? 'Takk! Vi har mottatt din forespørsel og kommer tilbake til deg snart.'
      : 'Takk! Vi har mottatt din timebestilling og kontakter deg snart.';
  }

  // Submit handler.
  //
  // The store has Shopify's storefront captcha (hCaptcha) enabled, and
  // /contact rejects any submission that has not passed it. The captcha
  // only runs as part of a NATIVE form submit (it ends by submitting the
  // form itself), so a valid send reloads the page — there is no AJAX
  // path through a captcha-protected /contact.
  //
  // So all we do here is gate the native submit on our own validation:
  //   • invalid form → cancel the submit (preventDefault) and
  //     stopImmediatePropagation so Shopify's captcha never runs on it;
  //   • valid form   → do nothing — let the event flow on to Shopify's
  //     captcha, which submits to /contact. Shopify redirects back with
  //     ?contact_posted=true and showSubmissionResult() shows the toast.
  document.addEventListener('submit', e => {
    const form = e.target;
    if (!form || typeof form.matches !== 'function') return;
    if (!form.matches('.form-panel form')) return;

    // Honeypot — a real user never fills this hidden field. Cancel the
    // submit silently so neither the captcha nor a native POST fires.
    const hp = form.querySelector('input[name="delga-extra"]');
    if (hp && hp.value.trim() !== '') {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    // Client-side validation. On failure, cancel the submit completely.
    const firstInvalid = validate(form);
    if (firstInvalid) {
      e.preventDefault();
      e.stopImmediatePropagation();
      firstInvalid.focus();
      return;
    }

    // Valid — let Shopify's captcha + native submit take over (the page
    // will reload). Remember which form was sent so the toast shown
    // after the reload uses the right wording.
    try { sessionStorage.setItem('delga-sent-form', form.id); }
    catch (_) { /* sessionStorage blocked — toast falls back to default */ }
  }, true);

  // Live-clear errors as the user fixes them (delegated)
  document.addEventListener('input', e => {
    const input = e.target;
    if (!input || !input.closest) return;
    const field = input.closest('.form-panel form .field');
    if (field && field.classList.contains('has-error')) setError(field, '');
  });

  // After a successful submit Shopify reloads the page with
  // ?contact_posted=true. Show the success toast (wording depends on
  // which form was sent, remembered in sessionStorage) and clean the URL.
  (function showSubmissionResult() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('contact_posted') !== 'true') return;

      let sentForm = '';
      try {
        sentForm = sessionStorage.getItem('delga-sent-form') || '';
        sessionStorage.removeItem('delga-sent-form');
      } catch (_) { /* sessionStorage blocked */ }

      // Defer slightly so the toast isn't fighting the page-load animations.
      setTimeout(() => showToast(successMessage({ id: sentForm }), 'success'), 200);

      // Strip the param so a refresh doesn't replay the toast.
      params.delete('contact_posted');
      const newSearch = params.toString();
      const cleanUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
    } catch (_) { /* URL API missing — ignore */ }
  })();

  /* ───────── Year (in case of future copyright drift) ───────── */
  const yr = $('[data-year]');
  if (yr) yr.textContent = String(new Date().getFullYear());

/* ───────── Video facade (click-to-play) ─────────
     Each Video section renders a poster + play button and a sibling
     <template class="video-embed"> holding the real player markup. On
     click we swap the facade for the player so no third-party iframe
     loads until the visitor actually wants to watch. */
  $$('.video-facade').forEach(facade => {
    if (facade.classList.contains('video-facade--placeholder')) return;
    facade.addEventListener('click', () => {
      const tpl = facade.parentElement && facade.parentElement.querySelector('template.video-embed');
      if (!tpl) return;
      const live = document.createElement('div');
      live.className = 'video-embed-live';
      live.appendChild(tpl.content.cloneNode(true));
      facade.replaceWith(live);
      const playable = live.querySelector('video');
      if (playable && typeof playable.play === 'function') {
        playable.play().catch(() => { /* autoplay blocked — controls are shown */ });
      }
    });
  });

})();

/* ── DELGA gallery+ : view switcher + density toggle (localStorage) + lightbox. Added 2026-06-10, additive. ── */
(function () {
  var VIEW_KEY = 'delga-gallery-view';
  var COLS_KEY = 'delga-gallery-cols';
  var COL_ICONS = {
    '1': '<rect x="4" y="5.5" width="16" height="13" rx="2.5" fill="currentColor"/>',
    '2': '<rect x="3.5" y="5.5" width="7" height="13" rx="2" fill="currentColor"/><rect x="13.5" y="5.5" width="7" height="13" rx="2" fill="currentColor"/>',
    '4': '<rect x="3" y="5.5" width="3.2" height="13" rx="1.3" fill="currentColor"/><rect x="7.6" y="5.5" width="3.2" height="13" rx="1.3" fill="currentColor"/><rect x="13.2" y="5.5" width="3.2" height="13" rx="1.3" fill="currentColor"/><rect x="17.8" y="5.5" width="3.2" height="13" rx="1.3" fill="currentColor"/>'
  };
  var COL_ORDER = ['1', '2', '4'];

  // 1) Per-section controls: view switcher + density toggle
  Array.prototype.forEach.call(document.querySelectorAll('.gallery'), function (section) {
    var grid = section.querySelector('[data-gallery-grid]');
    if (!grid) return;
    var sel = section.querySelector('[data-gallery-switcher]');
    var density = section.querySelector('[data-gallery-density]');

    function applyView(view) {
      grid.classList.remove('is-masonry', 'is-slider');
      if (view === 'masonry') grid.classList.add('is-masonry');
      else if (view === 'slider') grid.classList.add('is-slider');
      grid.setAttribute('data-view', view);
      if (density) density.hidden = (view !== 'grid');
    }

    var curCols = grid.getAttribute('data-cols') || '4';
    function applyCols(n) {
      curCols = n;
      grid.classList.remove('cols-1', 'cols-2', 'cols-4');
      grid.classList.add('cols-' + n);
      if (density) {
        var icon = density.querySelector('.gd-icon');
        if (icon) icon.innerHTML = COL_ICONS[n];
        density.setAttribute('data-cols', n);
        density.setAttribute('aria-label', 'Bilder per rad: ' + n + ' (klikk for å endre)');
      }
    }

    if (sel) {
      var view = grid.getAttribute('data-default-view') || 'grid';
      try {
        var v = localStorage.getItem(VIEW_KEY);
        if (v === 'grid' || v === 'masonry' || v === 'slider') view = v;
      } catch (e) {}
      sel.value = view;
      applyView(view);
      sel.addEventListener('change', function () {
        applyView(sel.value);
        try { localStorage.setItem(VIEW_KEY, sel.value); } catch (e) {}
      });
    }

    if (density) {
      var cols = grid.getAttribute('data-cols') || '4';
      try {
        var c = localStorage.getItem(COLS_KEY);
        if (c === '1' || c === '2' || c === '4') cols = c;
      } catch (e) {}
      applyCols(cols);
      density.addEventListener('click', function () {
        var next = COL_ORDER[(COL_ORDER.indexOf(curCols) + 1) % COL_ORDER.length];
        applyCols(next);
        try { localStorage.setItem(COLS_KEY, next); } catch (e2) {}
      });
    }
  });

  // 2) Lightbox — for grids marked data-lightbox
  var grids = Array.prototype.slice.call(document.querySelectorAll('[data-gallery-grid][data-lightbox]'));
  if (!grids.length) return;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var lb = document.createElement('div');
  lb.className = 'lightbox';
  lb.setAttribute('role', 'dialog');
  lb.setAttribute('aria-modal', 'true');
  lb.setAttribute('aria-label', 'Bildevisning');
  lb.hidden = true;
  lb.innerHTML =
    '<button class="lightbox-close" type="button" data-lb-close aria-label="Lukk bildevisning">' +
      '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><path fill="currentColor" d="M19 6.4 17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12 19 6.4Z"/></svg>' +
    '</button>' +
    '<button class="lightbox-nav lightbox-prev" type="button" data-lb-prev aria-label="Forrige bilde">' +
      '<svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true"><path fill="currentColor" d="M15.4 7.4 14 6l-6 6 6 6 1.4-1.4L10.8 12z"/></svg>' +
    '</button>' +
    '<figure class="lightbox-stage"><img class="lightbox-img" alt=""><figcaption class="lightbox-caption"></figcaption></figure>' +
    '<button class="lightbox-nav lightbox-next" type="button" data-lb-next aria-label="Neste bilde">' +
      '<svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true"><path fill="currentColor" d="M8.6 16.6 10 18l6-6-6-6-1.4 1.4L13.2 12z"/></svg>' +
    '</button>';
  document.body.appendChild(lb);
  var img = lb.querySelector('.lightbox-img');
  var cap = lb.querySelector('.lightbox-caption');

  var items = [];
  var index = 0;
  var lastFocus = null;

  function show(i) {
    index = (i + items.length) % items.length;
    var it = items[index];
    img.setAttribute('src', it.src);
    img.setAttribute('alt', it.alt);
    cap.textContent = it.caption;
    cap.style.display = it.caption ? '' : 'none';
  }
  function open(set, i) {
    items = set;
    lastFocus = document.activeElement;
    show(i);
    lb.hidden = false;
    document.body.classList.add('lb-open');
    void lb.offsetWidth;
    lb.classList.add('is-open');
    var c = lb.querySelector('[data-lb-close]');
    if (c) c.focus();
  }
  function close() {
    lb.classList.remove('is-open');
    document.body.classList.remove('lb-open');
    var done = function () { lb.hidden = true; };
    if (reduce) { done(); } else { window.setTimeout(done, 200); }
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  grids.forEach(function (grid) {
    var figures = Array.prototype.slice.call(grid.querySelectorAll('.gallery-item'));
    var set = figures.map(function (fig) {
      var im = fig.querySelector('img');
      var fc = fig.querySelector('figcaption');
      return {
        src: im ? im.getAttribute('src') : '',
        alt: im ? (im.getAttribute('alt') || '') : '',
        caption: fc ? fc.textContent : ''
      };
    });
    figures.forEach(function (fig, i) {
      fig.setAttribute('tabindex', '0');
      fig.setAttribute('role', 'button');
      fig.setAttribute('aria-label', 'Åpne bilde: ' + (set[i].caption || set[i].alt));
      fig.addEventListener('click', function () { open(set, i); });
      fig.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(set, i); }
      });
    });
  });

  lb.addEventListener('click', function (e) {
    if (e.target.closest('[data-lb-close]')) { close(); return; }
    if (e.target.closest('[data-lb-prev]')) { show(index - 1); return; }
    if (e.target.closest('[data-lb-next]')) { show(index + 1); return; }
    if (!e.target.closest('.lightbox-stage') && !e.target.closest('.lightbox-nav')) { close(); }
  });
  document.addEventListener('keydown', function (e) {
    if (lb.hidden) return;
    if (e.key === 'Escape') { close(); }
    else if (e.key === 'ArrowLeft') { show(index - 1); }
    else if (e.key === 'ArrowRight') { show(index + 1); }
  });
})();
